import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@longy/web-components'
import { Link } from '@tanstack/react-router'
import { Stethoscope, Video } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const TILE_CLASS =
    'group flex flex-1 flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-6 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-xl'

type BookingTypeModalProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * The chooser behind the header's Időpontfoglalás button.
 *
 * Controlled rather than wrapped around a `DialogTrigger`: there are two buttons that open it — one
 * in the bar and one in the hamburger menu — but only one dialog, and the mobile button has to close
 * the menu in the same click, which a trigger cannot express.
 *
 * Signing in is not this component's problem. `/booking` already bounces a visitor without a token
 * to `/login?redirect=…`, and the login, registration, 2FA and social paths all honour that, so a
 * plain `Link` takes a logged-out visitor through the gate and back into the flow they picked.
 */
export const BookingTypeModal = ({ open, onOpenChange }: BookingTypeModalProps) => {
    const { t } = useTranslation()

    const close = () => onOpenChange(false)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    {/* The same words as the button that opened it — one label, one key. */}
                    <DialogTitle className="text-2xl font-bold text-brand-dark">
                        {t('frontend.header.bookAppointment')}
                    </DialogTitle>
                    <DialogDescription className="text-brand-gray">
                        {t('frontend.header.bookingModalLead')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4 pt-2 sm:flex-row">
                    <Link to="/booking/video-consultation" className={TILE_CLASS} onClick={close}>
                        <Video className="size-9 text-primary" />
                        <h3 className="text-lg font-bold text-brand-dark">
                            {t('frontend.header.bookingVideoTitle')}
                        </h3>
                        <p className="text-sm text-brand-gray">
                            {t('frontend.header.bookingVideoLead')}
                        </p>
                    </Link>

                    <Link to="/booking/examination" className={TILE_CLASS} onClick={close}>
                        <Stethoscope className="size-9 text-primary" />
                        <h3 className="text-lg font-bold text-brand-dark">
                            {t('frontend.header.bookingExaminationTitle')}
                        </h3>
                        <p className="text-sm text-brand-gray">
                            {t('frontend.header.bookingExaminationLead')}
                        </p>
                    </Link>
                </div>
            </DialogContent>
        </Dialog>
    )
}
